import { initConsoleCapture } from "@sd/lib/sdConsoleLogs";
import { installSecureLocalStorageGuard } from "@/lib/secureLocalStorage";
// Start capturing console logs as early as possible
initConsoleCapture();
installSecureLocalStorageGuard();

import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { RecoilRoot } from "recoil";
import { Route, Routes, useNavigate, Outlet } from "react-router-dom";
import { AuthProvider, useAuth } from "@sd/hooks/useAuth";
import SDLogin from "@sd/pages/SDLogin";
import SDForgotPassword from "@sd/pages/SDForgotPassword";
import SDForgotPasswordReset from "@sd/pages/SDForgotPasswordReset";
import SdSsoHandoff from "@sd/pages/SdSsoHandoff";
import Tickets from "@sd/pages/Tickets";
import AssetManagement from "@sd/pages/AssetManagement";
import KnowledgeBase from "@sd/pages/KnowledgeBase";
import Reports from "@sd/pages/Reports";
import Projects from "@sd/pages/Projects";
import NewTicket from "@sd/pages/NewTicket";
import UserProfile from "@sd/pages/UserProfile";
import Supervisor from "@sd/pages/Supervisor";
import Admin from "@sd/pages/Admin";
import CustomerPortal from "@sd/pages/CustomerPortal";
import UserPortalHome from "@sd/pages/UserPortalHome";
import TicketDetailPage from "@sd/pages/TicketDetailPage";
import TicketEmailDeepLinkPage from "@sd/pages/TicketEmailDeepLinkPage";
import NotFound from "@sd/pages/NotFound";
import { CompaniesAdminProvider } from "@sd/context/CompaniesAdminContext";
import { AdminCompanyMembersPage } from "@sd/components/admin/AdminCompanyMembers";
import SDProtectedRoute from "@sd/components/SDProtectedRoute";
import SDRoleGuard from "@sd/components/SDRoleGuard";
import { AgentPathGuard } from "@sd/components/AgentPathGuard";
import { isAgentShellRole } from "@sd/lib/sdRoles";
import { A1EmbeddedShell } from "@sd/components/A1EmbeddedShell";
import SchemaFormPage from "@/pages/SchemaManagement/SchemasListPage/SchemaFormPage";
import ImportAddPage from "@/pages/ImportManagement/ImportAddPage";
import ImportDetailPage from "@/pages/ImportManagement/ImportDetailPage";
import UserFormPage from "@/pages/UserManagement/UserListPage/UserFormPage";
import RoleFormPage from "@/pages/UserManagement/RoleListPage/RoleFormPage";
import FromSchemasRenderPage from "@/pages/SchemaManagement/FromSchemasRenderPage";
import ModulePage from "@/pages/SchemaManagement/ModulePage";
import ModuleFormPage from "@/pages/SchemaManagement/ModulePage/ModuleFormPage";
import PrintPage from "@/pages/PrintPage";
import { useEffect } from "react";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
    mutations: { retry: false },
  },
});

const RoleBasedRedirect = () => {
  const { role, isLoading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (isLoading) return;
    if (role === "ServiceDesk_Admin") navigate("admin", { replace: true });
    else if (isAgentShellRole(role)) navigate("tickets/my-tickets", { replace: true });
    else if (role === "ServiceDesk_SuperAgent") navigate("tickets/project-status", { replace: true });
    else if (role === "ServiceDesk_User") navigate("my-portal", { replace: true });
  }, [role, isLoading, navigate]);

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-center">
          <div className="mb-3 mx-auto h-8 w-8 animate-spin rounded-full border-4 border-slate-400 border-t-slate-700" />
          <p className="text-sm text-slate-500">Loading your portal…</p>
        </div>
      </div>
    );
  }

  if (!role) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-center max-w-sm px-4">
          <h2 className="text-lg font-semibold text-slate-800">Access Denied</h2>
          <p className="mt-1 text-sm text-slate-500">
            You do not have a Service Desk role. Contact your administrator.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen items-center justify-center bg-background">
      <div className="text-center">
        <div className="mb-3 mx-auto h-8 w-8 animate-spin rounded-full border-4 border-slate-400 border-t-slate-700" />
        <p className="text-sm text-slate-500">Redirecting…</p>
      </div>
    </div>
  );
};

const ServiceDeskApp = () => (
  <RecoilRoot>
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
      <Routes>
        <Route path="login" element={<SDLogin />} />
        <Route path="forgot-password" element={<SDForgotPassword />} />
        <Route
          path="forgot-password-reset/:id"
          element={<SDForgotPasswordReset />}
        />
        <Route path="sso-handoff" element={<SdSsoHandoff />} />

        <Route element={<SDProtectedRoute />}>
          <Route element={<AgentPathGuard />}>
          <Route index element={<RoleBasedRedirect />} />

          <Route
            element={
              <SDRoleGuard allowed={["ServiceDesk_Admin"]} />
            }
          >
            <Route
              path="admin/module/:schemaName/edit/:id"
              element={
                <A1EmbeddedShell>
                  <ModuleFormPage />
                </A1EmbeddedShell>
              }
            />
            <Route
              path="admin/module/:schemaName/add"
              element={
                <A1EmbeddedShell>
                  <ModuleFormPage />
                </A1EmbeddedShell>
              }
            />
            <Route
              path="admin/module/:schemaName"
              element={
                <A1EmbeddedShell>
                  <ModulePage />
                </A1EmbeddedShell>
              }
            />
            <Route
              path="admin/from/:schemaName"
              element={
                <A1EmbeddedShell>
                  <FromSchemasRenderPage />
                </A1EmbeddedShell>
              }
            />
            <Route
              path="admin/print/:schemaName/:recordId"
              element={
                <A1EmbeddedShell>
                  <PrintPage />
                </A1EmbeddedShell>
              }
            />
            <Route
              path="admin/schemas/add"
              element={
                <A1EmbeddedShell>
                  <SchemaFormPage />
                </A1EmbeddedShell>
              }
            />
            <Route
              path="admin/schemas/edit/:schemaName"
              element={
                <A1EmbeddedShell>
                  <SchemaFormPage />
                </A1EmbeddedShell>
              }
            />
            <Route
              path="admin/imports/add"
              element={
                <A1EmbeddedShell>
                  <ImportAddPage />
                </A1EmbeddedShell>
              }
            />
            <Route
              path="admin/imports/:importId"
              element={
                <A1EmbeddedShell>
                  <ImportDetailPage />
                </A1EmbeddedShell>
              }
            />
            <Route
              path="admin/tenant/users/add"
              element={
                <A1EmbeddedShell>
                  <UserFormPage />
                </A1EmbeddedShell>
              }
            />
            <Route
              path="admin/tenant/users/edit/:id"
              element={
                <A1EmbeddedShell>
                  <UserFormPage />
                </A1EmbeddedShell>
              }
            />
            <Route
              path="admin/tenant/roles/add"
              element={
                <A1EmbeddedShell>
                  <RoleFormPage />
                </A1EmbeddedShell>
              }
            />
            <Route
              path="admin/tenant/roles/edit/:id"
              element={
                <A1EmbeddedShell>
                  <RoleFormPage />
                </A1EmbeddedShell>
              }
            />
            <Route element={<CompaniesAdminProvider><Outlet /></CompaniesAdminProvider>}>
              <Route path="admin/companies/:companyId/members" element={<AdminCompanyMembersPage />} />
              <Route path="admin" element={<Admin />} />
              <Route path="admin/:view" element={<Admin />} />
            </Route>
            <Route path="supervisor" element={<Supervisor />} />
            <Route path="supervisor/:view" element={<Supervisor />} />
            <Route path="reports" element={<Reports />} />
          </Route>

          <Route
            element={
              <SDRoleGuard
                allowed={[
                  "ServiceDesk_Admin",
                  "ServiceDesk_Agent",
                  "ServiceDesk_SuperAgent",
                  "ServiceDesk_User",
                  "ServiceDesk_External_Agent",
                  "ServiceDesk_External_Customer",
                ]}
              />
            }
          >
            <Route path="ticket/:ticketId" element={<TicketEmailDeepLinkPage />} />
            <Route path="tickets/view/:ticketId" element={<TicketDetailPage />} />
          </Route>

          <Route
            element={
              <SDRoleGuard
                allowed={[
                  "ServiceDesk_Admin",
                  "ServiceDesk_Agent",
                  "ServiceDesk_SuperAgent",
                  "ServiceDesk_External_Agent",
                  "ServiceDesk_External_Customer",
                ]}
              />
            }
          >
            <Route path="tickets" element={<Tickets />} />
            <Route path="tickets/:queue" element={<Tickets />} />
            <Route path="tickets/new" element={<NewTicket />} />
            <Route path="projects" element={<Projects />} />
            <Route path="assets" element={<AssetManagement />} />
            <Route path="assets/:queue" element={<AssetManagement />} />
          </Route>

          <Route
            element={
              <SDRoleGuard
                allowed={[
                  "ServiceDesk_Admin",
                  "ServiceDesk_Agent",
                  "ServiceDesk_SuperAgent",
                  "ServiceDesk_User",
                  "ServiceDesk_External_Agent",
                  "ServiceDesk_External_Customer",
                ]}
              />
            }
          >
            <Route path="knowledge-base" element={<KnowledgeBase />} />
            <Route path="users/:id" element={<UserProfile />} />
          </Route>

          <Route
            element={
              <SDRoleGuard
                allowed={[
                  "ServiceDesk_Admin",
                  "ServiceDesk_Agent",
                  "ServiceDesk_SuperAgent",
                  "ServiceDesk_External_Agent",
                  "ServiceDesk_External_Customer",
                ]}
              />
            }
          >
            <Route path="customer" element={<CustomerPortal />} />
            <Route path="customer/:companyId" element={<CustomerPortal />} />
          </Route>

          {/* Email deep link: /sd/{ticket-uuid} → detail page (must stay last in protected routes). */}
          <Route path=":ticketUuid" element={<TicketEmailDeepLinkPage />} />
          </Route>
        </Route>

        <Route path="my-portal" element={<UserPortalHome />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </AuthProvider>
  </QueryClientProvider>
  </RecoilRoot>
);

export default ServiceDeskApp;
